/**
 * The one HTTP adapter for the platform surface.
 *
 * Mirrors apps/api/src/routes/apps.ts and routes/commands.ts one-for-one. No
 * fixtures and no mock variant: an integration screen that renders invented
 * webhooks would be actively harmful — the entire value of this page is telling
 * an operator the truth about which credentials exist and when they were last
 * used.
 */

import type {
  AppDetail,
  AppSummary,
  BotTokenCreated,
  BotTokenSummary,
  CreateAppInput,
  CreateBotTokenInput,
  CreateEventSubscriptionInput,
  CreateIncomingWebhookInput,
  CreateMessageActionInput,
  CreateSlashCommandInput,
  EventSubscription,
  EventSubscriptionCreated,
  IncomingWebhook,
  IncomingWebhookCreated,
  MessageAction,
  MessageActionResult,
  SlashCommand,
  TabCatalogueEntry,
  WebhookDelivery,
} from '@cyphex/types/platform';
import type { ChannelId, MessageId, Uuid } from '@cyphex/types/common';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/lib/api/client';
import { query } from '@/lib/api/query';

export interface DeliveryPage {
  deliveries: WebhookDelivery[];
  nextCursor: string | null;
}

export const platformApi = {
  /* ---- Apps & bots ------------------------------------------------------- */

  listApps: (signal?: AbortSignal): Promise<AppSummary[]> =>
    apiGet<{ apps: AppSummary[] }>('/apps', signal).then((payload) => payload.apps ?? []),

  getApp: (appId: Uuid, signal?: AbortSignal): Promise<AppDetail> =>
    apiGet<AppDetail>(`/apps/${appId}`, signal),

  createApp: (input: CreateAppInput): Promise<AppSummary> =>
    apiPost<AppSummary>('/apps', input),

  deleteApp: (appId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/apps/${appId}`).then(() => undefined),

  /* ---- Bot tokens -------------------------------------------------------- */

  listTokens: (signal?: AbortSignal): Promise<BotTokenSummary[]> =>
    apiGet<{ tokens: BotTokenSummary[] }>('/apps/tokens', signal).then(
      (payload) => payload.tokens ?? [],
    ),

  /** The response is the ONLY time the secret exists outside the server. */
  createToken: (input: CreateBotTokenInput): Promise<BotTokenCreated> =>
    apiPost<BotTokenCreated>('/apps/tokens', input),

  revokeToken: (tokenId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/apps/tokens/${tokenId}`).then(() => undefined),

  /* ---- Incoming webhooks -------------------------------------------------- */

  listWebhooks: (signal?: AbortSignal): Promise<IncomingWebhook[]> =>
    apiGet<{ webhooks: IncomingWebhook[] }>('/apps/webhooks', signal).then(
      (payload) => payload.webhooks ?? [],
    ),

  createWebhook: (input: CreateIncomingWebhookInput): Promise<IncomingWebhookCreated> =>
    apiPost<IncomingWebhookCreated>('/apps/webhooks', input),

  rotateWebhook: (webhookId: Uuid): Promise<{ id: Uuid; url: string }> =>
    apiPost<{ id: Uuid; url: string }>(`/apps/webhooks/${webhookId}/rotate`),

  setWebhookEnabled: (webhookId: Uuid, enabled: boolean): Promise<void> =>
    apiPatch<{ ok: boolean }>(`/apps/webhooks/${webhookId}`, { enabled }).then(() => undefined),

  revokeWebhook: (webhookId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/apps/webhooks/${webhookId}`).then(() => undefined),

  /* ---- Event subscriptions ------------------------------------------------ */

  listSubscriptions: (signal?: AbortSignal): Promise<EventSubscription[]> =>
    apiGet<{ subscriptions: EventSubscription[] }>('/apps/subscriptions', signal).then(
      (payload) => payload.subscriptions ?? [],
    ),

  createSubscription: (
    input: CreateEventSubscriptionInput,
  ): Promise<EventSubscriptionCreated> =>
    apiPost<EventSubscriptionCreated>('/apps/subscriptions', input),

  updateSubscription: (
    subscriptionId: Uuid,
    input: { name?: string; eventTypes?: string[]; enabled?: boolean },
  ): Promise<void> =>
    apiPatch<{ ok: boolean }>(`/apps/subscriptions/${subscriptionId}`, input).then(
      () => undefined,
    ),

  deleteSubscription: (subscriptionId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/apps/subscriptions/${subscriptionId}`).then(() => undefined),

  listDeliveries: (
    subscriptionId: Uuid,
    options: { status?: string; cursor?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<DeliveryPage> =>
    apiGet<DeliveryPage>(
      `/apps/subscriptions/${subscriptionId}/deliveries${query({
        status: options.status ?? '',
        cursor: options.cursor ?? '',
        limit: options.limit ?? 25,
      })}`,
      signal,
    ),

  replayDelivery: (deliveryId: Uuid): Promise<void> =>
    apiPost<{ ok: boolean }>(`/apps/deliveries/${deliveryId}/replay`).then(() => undefined),

  /* ---- Slash commands ----------------------------------------------------- */

  /** Management view: includes disabled rows and the built-ins, flagged. */
  listManagedCommands: (signal?: AbortSignal): Promise<SlashCommand[]> =>
    apiGet<{ commands: SlashCommand[] }>('/apps/commands', signal).then(
      (payload) => payload.commands ?? [],
    ),

  createCommand: (
    input: CreateSlashCommandInput,
  ): Promise<{ id: Uuid; command: string; signingSecret: string }> =>
    apiPost<{ id: Uuid; command: string; signingSecret: string }>('/apps/commands', input),

  setCommandEnabled: (commandId: Uuid, enabled: boolean): Promise<void> =>
    apiPatch<{ ok: boolean }>(`/apps/commands/${commandId}`, { enabled }).then(() => undefined),

  deleteCommand: (commandId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/apps/commands/${commandId}`).then(() => undefined),

  /* ---- Message actions ---------------------------------------------------- */

  listManagedMessageActions: (signal?: AbortSignal): Promise<MessageAction[]> =>
    apiGet<{ actions: MessageAction[] }>('/apps/message-actions', signal).then(
      (payload) => payload.actions ?? [],
    ),

  createMessageAction: (
    input: CreateMessageActionInput,
  ): Promise<{ id: Uuid; signingSecret: string }> =>
    apiPost<{ id: Uuid; signingSecret: string }>('/apps/message-actions', input),

  deleteMessageAction: (actionId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/apps/message-actions/${actionId}`).then(() => undefined),

  /* ---- Composer-facing (every member, not just administrators) ------------- */

  listCommands: (signal?: AbortSignal): Promise<SlashCommand[]> =>
    apiGet<{ commands: SlashCommand[] }>('/commands', signal).then(
      (payload) => payload.commands ?? [],
    ),

  listMessageActions: (signal?: AbortSignal): Promise<MessageAction[]> =>
    apiGet<{ actions: MessageAction[] }>('/commands/message-actions', signal).then(
      (payload) => payload.actions ?? [],
    ),

  invokeMessageAction: (actionId: Uuid, messageId: MessageId): Promise<MessageActionResult> =>
    apiPost<MessageActionResult>(`/commands/message-actions/${actionId}/invoke`, { messageId }),

  /* ---- Channel tabs -------------------------------------------------------- */

  tabCatalogue: (channelId: ChannelId, signal?: AbortSignal): Promise<TabCatalogueEntry[]> =>
    apiGet<{ entries: TabCatalogueEntry[] }>(
      `/channels/${channelId}/tab-catalogue`,
      signal,
    ).then((payload) => payload.entries ?? []),

  createTab: (
    channelId: ChannelId,
    input: { kind: string; name: string; icon?: string; config?: Record<string, string> },
  ): Promise<{ id: Uuid }> => apiPost<{ id: Uuid }>(`/channels/${channelId}/tabs`, input),

  deleteTab: (channelId: ChannelId, tabId: Uuid): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/channels/${channelId}/tabs/${tabId}`).then(() => undefined),

  reorderTabs: (channelId: ChannelId, tabIds: Uuid[]): Promise<void> =>
    apiPut<{ ok: boolean }>(`/channels/${channelId}/tabs/order`, { tabIds }).then(
      () => undefined,
    ),
} as const;
